import { Check, CheckCheck, ChevronDown, ChevronUp, Pause, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { ApiError, type Entity, type Transcript, type TranscriptSegment, type TranscriptSpeaker } from "@/lib/api";
import { NamePicker } from "@/components/NamePicker";
import { formatElapsed, formatTimecode } from "@/lib/format";
import { speakerColour, speakerInitials, speakerName } from "@/lib/speakerColour";
import { useAudioFragment } from "@/lib/useAudioFragment";
import { SpeakerTurns } from "@/pages/recording/SpeakerTurns";

/**
 * SPCH-6 — the Speakers tab, where the owner listens and says who each voice is.
 *
 * The gallery already proposed (SPCH-5): a recognised voice arrives with a name
 * pre-filled and a confidence badge, an unrecognised one arrives "Unmatched". Either
 * way the owner confirms by ear — the point of the play button beside each row — and
 * confirming is what adds this recording's samples to the gallery the next meeting is
 * matched against.
 *
 * **A row opens.** For a long time the whole evidence for a voice was one fragment: the
 * row played that speaker's longest turn and nothing else. That is the one turn least
 * safe to judge a cluster by — when the diarizer drops somebody else's long answer into
 * this voice, the intruder *becomes* the longest turn, so the sample the owner hears is
 * precisely the wrong one and there is no second opinion anywhere on the screen.
 * Expanding a row lists the turns the cluster is actually made of, longest first, each
 * one playable, with three spread across the meeting offered at the top. A cluster
 * holding two voices stops being a suspicion and becomes something you can hear in
 * fifteen seconds.
 */
export function SpeakersTab({
  transcript,
  player,
  persons,
  onConfirm,
}: {
  transcript: Transcript | null;
  player: ReturnType<typeof useAudioFragment>;
  persons: Entity[];
  onConfirm: (speakerId: string, who: { personId: string } | { name: string }) => Promise<void>;
}) {
  // Grouped once for the whole tab rather than filtered per row: five rows over a
  // three-hour meeting is five passes over several thousand turns, repeated on every
  // confirmation.
  const turns = useMemo(() => {
    const grouped = new Map<string, TranscriptSegment[]>();

    for (const segment of transcript?.segments ?? []) {
      if (segment.speakerId === null) continue;

      const known = grouped.get(segment.speakerId);
      if (known) known.push(segment);
      else grouped.set(segment.speakerId, [segment]);
    }

    return grouped;
  }, [transcript]);

  if (!transcript || transcript.speakers.length === 0) {
    return (
      <p className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        The diarizer found nobody in this recording.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {player.problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {player.problem}
        </p>
      )}

      <ul aria-label="Speakers" className="flex flex-col gap-[var(--space-2)]">
        {transcript.speakers.map((speaker, index) => (
          <li key={speaker.id}>
            <SpeakerRow
              speaker={speaker}
              index={index}
              turns={turns.get(speaker.id) ?? []}
              player={player}
              persons={persons}
              // Who "sounds like" points at, resolved here where the whole list is in
              // hand — the row only knows an id, and what it needs to draw is a name and
              // a position (the position is what the colour and "Speaker 3" come from).
              neighbour={neighbourOf(speaker, transcript.speakers)}
              onConfirm={onConfirm}
            />
          </li>
        ))}
      </ul>

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        Play a sample of each voice, then confirm who it is. Recognition improves with every meeting you confirm — so
        open a voice and listen to more than one turn before you do.
      </p>
    </div>
  );
}

/**
 * The voice this one sounds like, as the row needs it: a name to offer, a position to
 * number it by, and whether it has already been confirmed to somebody.
 *
 * Null unless the machine found a neighbour close enough to be worth a suggestion — and
 * null again if that neighbour has been reassigned away since, because a merge offer
 * pointing at a voice that no longer exists is worse than no offer.
 */
function neighbourOf(
  speaker: TranscriptSpeaker,
  all: TranscriptSpeaker[],
): { speaker: TranscriptSpeaker; index: number } | null {
  if (speaker.soundsLikeId === null) return null;

  const index = all.findIndex((candidate) => candidate.id === speaker.soundsLikeId);
  return index < 0 ? null : { speaker: all[index]!, index };
}

/** The confidence badge over a gallery match — the design's High / Medium / Unmatched. */
function matchBadge(speaker: TranscriptSpeaker): { label: string; tone: string } {
  if (speaker.matchedPersonId === null || speaker.matchScore === null)
    return { label: "Unmatched", tone: "var(--text-muted)" };

  // Mirrors SpeakerMatch.HighSimilarity on the Server — the same line the proposal was
  // drawn on, so the badge and the decision never disagree. It moved with the embedding
  // model (0038): a threshold belongs to the model that produced the score.
  return speaker.matchScore >= 0.75
    ? { label: "High", tone: "var(--vl-life-soft)" }
    : { label: "Medium", tone: "var(--vl-accent-warm)" };
}

function SpeakerRow({
  speaker,
  index,
  turns,
  player,
  persons,
  neighbour,
  onConfirm,
}: {
  speaker: TranscriptSpeaker;
  index: number;
  turns: TranscriptSegment[];
  player: ReturnType<typeof useAudioFragment>;
  persons: Entity[];
  /** The voice this one sounds most like, when the machine found one worth offering. */
  neighbour: { speaker: TranscriptSpeaker; index: number } | null;
  onConfirm: (speakerId: string, who: { personId: string } | { name: string }) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const colour = speakerColour(index);
  const confirmed = speaker.status === "confirmed";
  // The name to show: what a human confirmed, else the gallery's proposal, else the
  // position. The pre-filled proposal is a suggestion until confirmed, never an identity.
  const name = speaker.confirmedName ?? speaker.matchedPersonName ?? speakerName(speaker, index);
  const initials = speakerInitials({ confirmedName: speaker.confirmedName ?? speaker.matchedPersonName }, index);
  const playing = player.playing === speaker.id;
  const sample = speaker.sampleStartMs;
  const badge = matchBadge(speaker);

  // The merge offer, and the whole reason the diarizer is allowed to split where it is
  // unsure: joining two rows has to be one click. It only appears once the *other* row is
  // confirmed, because merging means "this is that person too" and there is no person to
  // point at until somebody has said who that voice is.
  const mergeInto =
    !confirmed && neighbour !== null && neighbour.speaker.personId !== null
      ? {
          personId: neighbour.speaker.personId,
          name:
            neighbour.speaker.confirmedName
            ?? neighbour.speaker.matchedPersonName
            ?? speakerName(neighbour.speaker, neighbour.index),
        }
      : null;

  async function run(who: { personId: string } | { name: string }) {
    setBusy(true);
    setProblem(null);
    try {
      await onConfirm(speaker.id, who);
      setEditing(false);
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex items-center gap-[var(--space-4)]">
        <span
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-full)] text-[length:var(--text-xs)] font-bold"
          style={{ background: `color-mix(in oklch, ${colour} 22%, transparent)`, color: colour }}
        >
          {initials}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[length:var(--text-md)] font-semibold" style={{ color: colour }}>
            {name}
          </p>
          <p className="mt-0.5 text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {speaker.segmentCount} turn{speaker.segmentCount === 1 ? "" : "s"} ·{" "}
            {formatElapsed(speaker.speakingMs)} speaking
            {!confirmed && speaker.matchedPersonName !== null && <> · matched from the gallery</>}
            {/* Said on the closed row, not just inside the panel: a voice with no
                fingerprints teaches the gallery nothing when it is confirmed, and that
                is exactly the kind of nothing nobody goes looking for. */}
            {speaker.sampleCount === 0 && <> · <span className="text-[var(--warning)]">no voice samples</span></>}
            {/* The two facts the split check produced, both said on the closed row: a
                voice that sounds like another is one click from being merged into it, and
                a voice that is two people is the one thing here nobody should name until
                they have listened. */}
            {neighbour !== null && !confirmed && (
              <>
                {" "}· sounds like{" "}
                {neighbour.speaker.confirmedName
                  ?? neighbour.speaker.matchedPersonName
                  ?? speakerName(neighbour.speaker, neighbour.index)}
              </>
            )}
            {speaker.mixed && (
              <> · <span className="text-[var(--warning)]">sounds like more than one person</span></>
            )}
          </p>
        </div>

        {confirmed ? (
          <span className="shrink-0 rounded-[var(--radius-full)] bg-[color-mix(in_oklch,var(--vl-life-soft)_18%,transparent)] px-2.5 py-1 text-[length:var(--text-xs)] font-semibold text-[var(--vl-life-soft)]">
            Confirmed
          </span>
        ) : (
          <span
            className="shrink-0 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-2.5 py-1 text-[length:var(--text-xs)] font-semibold"
            style={{ color: badge.tone }}
          >
            {badge.label}
          </span>
        )}

        <button
          type="button"
          onClick={() => setOpen((shown) => !shown)}
          aria-expanded={open}
          aria-controls={`turns-${speaker.id}`}
          aria-label={open ? `Hide the turns of ${name}` : `Show the ${turns.length} turns of ${name}`}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-full)] text-[var(--text-secondary)] transition hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]"
        >
          {open ? <ChevronUp size={15} strokeWidth={2.5} /> : <ChevronDown size={15} strokeWidth={2.5} />}
        </button>

        {sample !== null && (
          <button
            type="button"
            onClick={() => player.play({ id: speaker.id, startMs: sample, endMs: speaker.sampleEndMs })}
            aria-label={playing ? `Stop ${name}` : `Play a sample of ${name} from ${formatTimecode(sample)}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)] text-[var(--vl-bridge-soft)] transition hover:brightness-125"
          >
            {playing ? <Pause size={14} strokeWidth={2.5} /> : <Play size={14} strokeWidth={2.5} />}
          </button>
        )}

        {confirmed ? (
          <button
            type="button"
            onClick={() => setEditing((open) => !open)}
            className="shrink-0 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
          >
            Change
          </button>
        ) : speaker.matchedPersonId !== null ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => run({ personId: speaker.matchedPersonId! })}
              className="inline-flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--vl-bridge)] px-2.5 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-inverse)] transition hover:brightness-110 disabled:opacity-60"
            >
              <Check size={13} strokeWidth={2.5} /> Confirm {speaker.matchedPersonName}
            </button>
            <button
              type="button"
              onClick={() => setEditing((open) => !open)}
              className="rounded-[var(--radius-md)] px-2 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
            >
              Someone else
            </button>
          </div>
        ) : mergeInto !== null ? (
          // No gallery match, but the voice next to it is already this person. Confirming
          // both to one person *is* the merge: the transcript renders a confirmed speaker
          // by name, so the two rows read as one from that moment on.
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => run({ personId: mergeInto.personId })}
              className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-2.5 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-60"
            >
              <CheckCheck size={13} strokeWidth={2.5} /> Also {mergeInto.name}
            </button>
            <button
              type="button"
              onClick={() => setEditing((open) => !open)}
              className="rounded-[var(--radius-md)] px-2 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
            >
              Someone else
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing((open) => !open)}
            className="shrink-0 rounded-[var(--radius-md)] border border-[var(--border-default)] px-2.5 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)]"
          >
            Name this speaker
          </button>
        )}
      </div>

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {editing && (
        <NameEditor
          speaker={speaker}
          index={index}
          persons={persons}
          busy={busy}
          onPick={run}
          onCancel={() => setEditing(false)}
        />
      )}

      {open && (
        <SpeakerTurns
          speaker={speaker}
          confirmed={confirmed}
          colour={colour}
          turns={turns}
          player={player}
        />
      )}
    </div>
  );
}

/**
 * The name-a-speaker control (SPCH-6), over the shared picker simple mode's Needs-you card
 * uses too — the question is this screen's, the answering is one component's.
 */
function NameEditor({
  speaker,
  index,
  persons,
  busy,
  onPick,
  onCancel,
}: {
  speaker: TranscriptSpeaker;
  index: number;
  persons: Entity[];
  busy: boolean;
  onPick: (who: { personId: string } | { name: string }) => void;
  onCancel: () => void;
}) {
  return (
    <NamePicker
      heading={`Who is ${speakerName(speaker, index)}?`}
      persons={persons}
      busy={busy}
      onPick={onPick}
      onCancel={onCancel}
    />
  );
}

// ------------------------------------------------------------------ insights (UI-5, M5.2)
